import React, {useRef, useState} from 'react';
import {Col, FormGroup, Input, Label, Row} from 'reactstrap';
import {Form} from '@unform/web';
import I18nMessage from '../../components/I18nMessage';
import * as Yup from 'yup';
import ReactSelect from '../../components/react-select';
import {TextArea} from '../../components/textarea';
import {Input as UInput} from '../../components/input';
import Button from '../../components/button';
import useStore from '../../hooks/use-store';
import {trigger} from '../../helpers/events';
import {useIntl} from 'react-intl';

const EssentialSinisterForm = (props) => {
    const {
        sinister,
        close,
        creating,
        loadIncidents,
        setSinister,
    } = props;

    const schema = Yup.object().shape({
        deviceId: Yup.number().required(),
        customerId: Yup.number().required(),
        openingType: Yup.string().required(),
        openingDescription: Yup.string().required(),
        openingLatitude: Yup.number().required(),
        openingLongitude: Yup.number().required(),
    });
    const intl = useIntl();
    const {store} = useStore();
    const {sinisterStore} = store;
    const [loading] = useState(false);
    const formRef = useRef(null);

    const handleSubmit = async (payload) => {
        try {
            await schema.validate(payload, {
                abortEarly: false,
            });
            payload = schema.cast(payload);
            const result = await sinisterStore.save(payload);
            setSinister(result);
            loadIncidents();
            close();
        } catch (err) {
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
            props?.dispatchAccordionResize && props.dispatchAccordionResize();
        }
    };

    const cancel = () => {
        loadIncidents();
        trigger('openEventTab:click');
    };

    return (
        <Form
            ref={formRef}
            initialData={{
                id: sinister.id,
                deviceId: sinister.deviceId,
                customerId: sinister.customerId,
                openingDate: sinister.openingDate,
                openingType: sinister.openingType,
                openingAddress: sinister.openingAddress,
                openingLatitude: sinister.openingLatitude,
                openingLongitude: sinister.openingLongitude,
                openingDescription: sinister.openingDescription,
            }}
            onSubmit={handleSubmit}
            schema={schema}>


            <UInput hidden type="number" name="deviceId" />
            <UInput hidden type="number" name="customerId" />

            <Label><I18nMessage id="sharedDevice" /></Label>
            <Row>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label for="groupId"><I18nMessage id="devicePlate" /></Label>
                        <Input
                            plaintext
                            value={sinister.device.attributes.plate}
                        />
                    </FormGroup>
                </Col>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label for="groupId"><I18nMessage id="deviceModel" /></Label>
                        <Input
                            plaintext
                            value={sinister.device.attributes.vehicleModel}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="positionAddress" /></Label>
                        <Input
                            plaintext
                            defaultValue={sinister.openingAddress}
                            type='text'
                            readOnly={!creating}
                            {...(creating && {
                                tag: UInput,
                                id: 'openingAddress',
                                name: 'openingAddress',
                            })}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="positionLatitude" /></Label>
                        <Input
                            plaintext
                            type="text"
                            inputMode='numeric'
                            pattern='\d*'
                            readOnly={!creating}
                            defaultValue={sinister.openingLatitude}
                            {...(creating && {
                                tag: UInput,
                                id: 'openingLatitude',
                                name: 'openingLatitude',
                            })}
                        />
                    </FormGroup>
                </Col>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="positionLongitude" /></Label>
                        <Input
                            plaintext
                            type="text"
                            inputMode='numeric'
                            pattern='\d*'
                            readOnly={!creating}
                            defaultValue={sinister.openingLongitude}
                            {...(creating && {
                                tag: UInput,
                                id: 'openingLongitude',
                                name: 'openingLongitude',
                            })}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Label><I18nMessage id="sharedCustomer" /></Label>
            <Row>
                <Col>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="customerName" /></Label>
                        <Input
                            plaintext
                            value={sinister.customer.name}
                        />
                    </FormGroup>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="customerPhone" /></Label>
                        <Input
                            plaintext
                            value={sinister.customer.phone}
                        />
                    </FormGroup>
                    <FormGroup className="custom-formGroup">
                        <Label><I18nMessage id="userEmail" /></Label>
                        <Input
                            plaintext
                            value={sinister.customer.email}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <FormGroup className="custom-formGroup">
                <Label for="name">{intl.formatMessage({id: 'sinisterTypeQuestion'})}*</Label>
                <ReactSelect
                    id={'openingType'}
                    name="openingType"
                    options={[
                        {id: 'robbery', title: intl.formatMessage({id: 'sinisterTypeRobbery'})},
                        {id: 'theft', title: intl.formatMessage({id: 'sinisterTypeTheft'})},
                        {id: 'misappropriation', title: intl.formatMessage({id: 'sinisterTypeMisappropriation'})},
                        {id: 'accident', title: intl.formatMessage({id: 'sinisterTypeAccident'})},
                        {id: 'other', title: intl.formatMessage({id: 'sinisterTypeOther'})},
                    ]}
                />
            </FormGroup>

            <FormGroup className="custom-formGroup">
                <Label for="name">{intl.formatMessage({id: 'sinisterDescription'})}</Label>
                <TextArea
                    type="text"
                    name="openingDescription"
                    id="openingDescription"
                />
            </FormGroup>

            {creating && <Row>
                <Col> <Button
                    color={'warning'}
                    disabled={loading}
                    className={'outline'}
                    size={'sm'}
                    onClick={() => cancel()}>
                    {intl.formatMessage({id: 'sharedCancel'})}
                </Button></Col>
                <Col>
                    <Button
                        color={'success'}
                        disabled={loading}
                        className={'outline'}
                        size={'sm'}
                        onClick={() => formRef.current.submitForm()}>
                        {intl.formatMessage({id: 'sharedSave'})}
                    </Button>
                </Col>
            </Row>}
        </Form>

    );
};

export default EssentialSinisterForm;
